/**
 * RosterTemplateLibrary - Template library modal for roster management
 * 
 * P1 Feature: Template-Based Smart Rostering
 * Allows admins to view, create, and manage roster templates.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Calendar,
  Clock,
  Users,
  Search,
  Plus,
  Trash2,
  Copy,
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  History,
  Building2,
  Timer
} from 'lucide-react';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function RosterTemplateLibrary({ 
  open, 
  onOpenChange, 
  siteId = null, 
  siteName = '',
  currentWeek = null,
  onSelectTemplate = null 
}) {
  const { api, isAdmin } = useAuth();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Generate form state
  const [targetWeek, setTargetWeek] = useState('');
  const [assignOfficers, setAssignOfficers] = useState(true);
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/roster/templates';
      const params = new URLSearchParams();
      if (siteId) params.append('site_id', siteId);
      if (search) params.append('search', search);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [api, siteId, search]);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      // Set default target week to next week
      if (currentWeek) {
        const nextWeek = new Date(currentWeek);
        nextWeek.setDate(nextWeek.getDate() + 7);
        setTargetWeek(nextWeek.toISOString().split('T')[0]);
      }
    }
  }, [open, fetchTemplates, currentWeek]);

  // Fetch template details
  const fetchTemplateDetail = async (templateId) => {
    try {
      const response = await api.get(`/roster/templates/${templateId}`);
      setSelectedTemplate(response.data);
      setShowDetail(true);
    } catch (error) {
      toast.error('Failed to load template details');
    }
  };

  // Delete template
  const handleDelete = async (templateId, templateName) => {
    if (!window.confirm(`Delete template "${templateName}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/roster/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
        setShowDetail(false);
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  // Generate from template
  const handleGenerate = async () => {
    if (!selectedTemplate || !targetWeek) {
      toast.error('Please select a template and target week');
      return;
    }
    
    try {
      setGenerating(true);
      const response = await api.post(`/roster/templates/${selectedTemplate.id}/generate`, {
        target_week_start: targetWeek,
        assign_default_officers: assignOfficers,
        overwrite_existing: overwriteExisting,
      });
      
      const { summary } = response.data;
      toast.success(
        `Generated ${summary.shifts_created} shifts (${summary.filled} filled, ${summary.unfilled} unfilled)`
      );
      
      setShowGenerateDialog(false);
      onOpenChange(false);
      
      // Callback to parent
      if (onSelectTemplate) {
        onSelectTemplate(selectedTemplate, targetWeek);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate roster';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-400" />
            Roster Templates
            {siteName && <Badge className="bg-zinc-700 text-zinc-300 ml-2">{siteName}</Badge>}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Create and manage reusable roster templates for quick week generation
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4">
          {/* Template List */}
          <div className="flex-1 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-[#09090b] border-[#3f3f46]"
              />
            </div>

            {/* Templates */}
            <ScrollArea className="h-[400px]">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No templates found</p>
                  <p className="text-sm">Save a week's roster as a template to get started</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map((template) => (
                    <Card
                      key={template.id}
                      className={`bg-[#09090b] border-[#3f3f46] hover:border-zinc-500 cursor-pointer transition-all ${
                        selectedTemplate?.id === template.id ? 'border-blue-500' : ''
                      }`}
                      onClick={() => fetchTemplateDetail(template.id)}
                      data-testid={`template-card-${template.id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-white truncate">
                              {template.template_name}
                            </h4>
                            <p className="text-xs text-zinc-500 truncate">
                              {template.site_name || 'Unknown Site'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className={template.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}>
                              {template.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-zinc-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {template.shift_count} shifts
                          </span>
                          <span className="flex items-center gap-1">
                            <Timer className="w-3 h-3" />
                            {template.total_hours}h
                          </span>
                          <span className="flex items-center gap-1">
                            <Copy className="w-3 h-3" />
                            {template.recurrence_type}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Template Detail Panel */}
          {selectedTemplate && showDetail && (
            <div className="w-80 bg-[#09090b] border border-[#3f3f46] rounded-lg p-4 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-white">{selectedTemplate.template_name}</h3>
                <p className="text-xs text-zinc-500 mt-1">{selectedTemplate.description || 'No description'}</p>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-[#18181b] p-2 rounded">
                  <div className="text-zinc-500">Shifts</div>
                  <div className="text-lg font-mono text-white">{selectedTemplate.shift_count}</div>
                </div>
                <div className="bg-[#18181b] p-2 rounded">
                  <div className="text-zinc-500">Hours</div>
                  <div className="text-lg font-mono text-white">{selectedTemplate.total_hours}h</div>
                </div>
              </div>

              {/* Shift Patterns */}
              <div>
                <h4 className="text-xs font-medium text-zinc-400 mb-2">Shift Patterns</h4>
                <ScrollArea className="h-[180px]">
                  <div className="space-y-1">
                    {selectedTemplate.shifts?.map((shift, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs bg-[#18181b] p-2 rounded">
                        <div>
                          <span className="text-white font-medium">{shift.day_name}</span>
                          <span className="text-zinc-500 ml-2">
                            {shift.start_time} - {shift.end_time}
                          </span>
                        </div>
                        <span className="text-zinc-400">{shift.hours}h</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-[#3f3f46]">
                <Button
                  onClick={() => setShowGenerateDialog(true)}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 h-8 text-xs"
                  data-testid="generate-from-template-btn"
                >
                  <Play className="w-3 h-3 mr-1" />
                  Generate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8"
                  onClick={() => handleDelete(selectedTemplate.id, selectedTemplate.template_name)}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Generate Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Generate Roster from Template</DialogTitle>
              <DialogDescription className="text-zinc-500">
                Create shifts for a specific week using "{selectedTemplate?.template_name}"
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Target Week Start (Monday)</Label>
                <Input
                  type="date"
                  value={targetWeek}
                  onChange={(e) => setTargetWeek(e.target.value)}
                  className="bg-[#09090b] border-[#3f3f46]"
                  data-testid="target-week-input"
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={assignOfficers}
                    onChange={(e) => setAssignOfficers(e.target.checked)}
                    className="rounded border-zinc-600"
                  />
                  <span className="text-zinc-300">Assign default officers from template</span>
                </label>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={overwriteExisting}
                    onChange={(e) => setOverwriteExisting(e.target.checked)}
                    className="rounded border-zinc-600"
                  />
                  <span className="text-zinc-300">Overwrite existing shifts (if any)</span>
                </label>
              </div>

              {selectedTemplate && (
                <div className="bg-[#09090b] p-3 rounded border border-[#3f3f46]">
                  <p className="text-xs text-zinc-500 mb-1">Template will create:</p>
                  <p className="text-sm text-white">
                    {selectedTemplate.shift_count} shifts • {selectedTemplate.total_hours} hours
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowGenerateDialog(false)}
                className="border-zinc-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                className="bg-blue-500 hover:bg-blue-600"
                disabled={generating || !targetWeek}
                data-testid="confirm-generate-btn"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Generate Roster
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
